/**
 * The two runs of a level (components/play/TwoRuns.tsx): what the first run
 * cost, the tips, the second run with a fresh agent, and what it saved.
 *
 * Glossary as in strings/agents.ts and strings/efficiency.ts. Claude Code,
 * `/model haiku` and model ids stay Latin. Nothing here promises a saving:
 * every figure is the learner's own, and a run that cost more says so.
 */
import type { Change } from '../level-advice';
import type { Locale } from '../locale';

const en = {
  run1: 'Run 1 · your agent as it is',
  runAfter: 'Your run with a tip',
  cost: 'What it cost',
  priced: (book: string) => `At API list prices (price book ${book}).`,
  reported: 'Counted from what Claude Code reported.',
  tokensIn: 'Tokens read',
  cached: (n: string) => `${n} of them from cache`,
  tokensOut: 'Tokens written',
  calls: 'Model calls',
  byModel: 'Cost by model',
  analysis: 'What drove the cost',
  topModel: (model: string, pct: string) => `${pct} of the cost went to ${model}.`,
  cacheShare: (pct: string) => `${pct} of what it read came from the prompt cache, which is billed at a fraction of the price.`,
  readRatio: (n: string) => `It read ${n} tokens for every token it wrote: most of the bill is context, not answers.`,
  tipsTitle: 'How to spend less on the next run',
  pointed: 'Your run points to this one',
  more: 'More ways',
  stepsTitle: 'Try it again in a new conversation',
  step1: 'Apply one tip. For the model tip, add --model haiku to the end of the command.',
  step2: 'Quit Claude Code, so the next agent remembers nothing of this run.',
  step3: 'Press Run it again, paste the command again, and brief your agent again.',
  replay: 'Run it again',
  savedTitle: 'You saved',
  lessThan: (pct: string) => `${pct} less than your first run`,
  moreTitle: 'This cost more',
  moreThan: (pct: string) => `${pct} more than your first run. Try another tip and run it again.`,
  same: 'The same cost as your first run. Try another tip and run it again.',
  before: 'First run',
  after: 'Since then',
  honest: 'Every run after the first counts here, failed ones included.',
  differenceTitle: 'What made the difference',
  firstRun: 'First run',
  latestRun: 'Latest run',
  perMillion: 'Cost per million tokens',
  modelOnly: (more: string, cheaper: string) => `Your latest run used ${more} more tokens, but each token cost ${cheaper} less. The saving came from the model, not from less work.`,
  lessWork: (fewer: string) => `A token cost about the same in both runs; the latest simply used ${fewer} fewer tokens.`,
  both: (fewer: string, cheaper: string) => `Your latest run used ${fewer} fewer tokens, and each token cost ${cheaper} less.`,
  mixed: (tokens: string, price: string) => `Tokens used: ${tokens}. Cost per token: ${price}.`,
  carriedOver: (n: string, m: string) => `Its first call already carried ${n} tokens, against ${m} on your first run: the agent seems to have continued an earlier session. Quit Claude Code and start it again for a fair comparison.`,
  monthly: (amount: string) => `If your agent work saved the same share, a US$100 monthly bill would come to about ${amount}.`,
  monthlyNote: 'An illustration from this one level, not a forecast.',
  varies: 'Agents vary: the same agent on the same level can cost noticeably more or less from one run to the next. Run it again to see whether the saving holds.',
  moreNote: 'On a short task the model is the biggest lever. These pay off on longer work, where context builds up.',
  measuredTitle: 'What we measured on this level',
  change: { 'smaller-model': 'A smaller model (Haiku)', 'focused-request': 'A focused request' } as Record<Change, string>,
  saved: (pct: string) => `${pct} cheaper`,
  dearer: (pct: string) => `${pct} dearer`,
  measuredNote: 'A few runs of each in our lab: in conversation where marked, the learner briefing and checking; otherwise the agent working alone from the whole brief. A lead to test in your own conversation, not a promise.',
  inConversation: 'in conversation',
  why: {
    'l13-make-the-tests-pass': 'A failing test that names one file is short, clear work. The smaller model fixed it just as well, and each of its tokens costs about a third as much, so here the model is the lever.',
    'l14-read-the-stack-trace': 'A stack trace points straight at the broken line, so the work is short and clear. The smaller model fixed it just as well, at about a third of the price per token.',
    'l17-who-broke-it': 'Bisecting is a procedure of many steps, and the smaller model lost its way: it needed 28 calls where the big model needed 9, so its cheaper tokens barely helped. Naming the method instead (bisect with the test, do not read diffs) cut the big model from 23 calls to 13.',
  } as Record<string, string>,
  useWhen: {
    'l13-make-the-tests-pass': 'Use it when the failure already points at the place to fix.',
    'l14-read-the-stack-trace': 'Use it when the error already points at the place to fix.',
    'l17-who-broke-it': 'Use it when the task is a long procedure: name the method rather than downsizing the model.',
  } as Record<string, string>,
  requestLabel: 'The method to add to your request',
  stepModel: 'Add --model haiku to the end of the command.',
  stepRequest: 'Give your agent the task as you did before, and add the method above.',
  failedRuns: (failed: number, runs: number) => `failed ${failed} of ${runs} runs`,
  stepKeep: 'Keep the model as it is: on this task neither change paid for itself. To see why, try one conversation with --model haiku and watch where it goes wrong.',
};

export const twoRunsCopy: Record<Locale, typeof en> = {
  en,
  ar: {
    run1: 'المحاولة 1 · وكيلك كما هو',
    runAfter: 'محاولتك مع نصيحة',
    cost: 'التكلفة',
    priced: (book: string) => `بأسعار API المعلنة (دفتر الأسعار ${book}).`,
    reported: 'محسوبة مما أبلغ عنه Claude Code.',
    tokensIn: 'الرموز المقروءة',
    cached: (n: string) => `${n} منها من الذاكرة المؤقتة`,
    tokensOut: 'الرموز المكتوبة',
    calls: 'طلبات النموذج',
    byModel: 'التكلفة حسب النموذج',
    analysis: 'ما الذي رفع التكلفة',
    topModel: (model: string, pct: string) => `ذهب ${pct} من التكلفة إلى ${model}.`,
    cacheShare: (pct: string) => `جاء ${pct} مما قرأه من الذاكرة المؤقتة للأوامر، وتُحسب بجزء صغير من السعر.`,
    readRatio: (n: string) => `قرأ ${n} رمزًا مقابل كل رمز كتبه: معظم الفاتورة سياق، لا إجابات.`,
    tipsTitle: 'كيف تنفق أقل في المحاولة التالية',
    pointed: 'محاولتك تشير إلى هذه',
    more: 'طرق أخرى',
    stepsTitle: en.stepsTitle,
    step1: 'طبّق نصيحة واحدة. لنصيحة النموذج أضف ‎--model haiku في آخر الأمر.',
    step2: 'أغلق Claude Code، حتى لا يتذكر الوكيل التالي شيئًا من هذه المحاولة.',
    step3: en.step3,
    replay: 'أعد المحاولة',
    savedTitle: 'وفّرت',
    lessThan: (pct: string) => `أقل بنسبة ${pct} من محاولتك الأولى`,
    moreTitle: 'كلّفت هذه أكثر',
    moreThan: (pct: string) => `أكثر بنسبة ${pct} من محاولتك الأولى. جرّب نصيحة أخرى وأعد المحاولة.`,
    same: 'التكلفة نفسها كمحاولتك الأولى. جرّب نصيحة أخرى وأعد المحاولة.',
    before: 'المحاولة الأولى',
    after: 'منذ ذلك الحين',
    honest: 'كل محاولة بعد الأولى محسوبة هنا، بما فيها الفاشلة.',
    differenceTitle: 'ما الذي صنع الفرق',
    firstRun: 'المحاولة الأولى',
    latestRun: 'آخر محاولة',
    perMillion: 'تكلفة كل مليون رمز',
    modelOnly: (more: string, cheaper: string) => `استخدمت آخر محاولة رموزًا أكثر بنسبة ${more}، لكن كل رمز كلّف أقل بنسبة ${cheaper}. جاء التوفير من النموذج، لا من عمل أقل.`,
    lessWork: (fewer: string) => `كلّف الرمز تقريبًا المبلغ نفسه في المحاولتين؛ آخر محاولة استخدمت ببساطة رموزًا أقل بنسبة ${fewer}.`,
    both: (fewer: string, cheaper: string) => `استخدمت آخر محاولة رموزًا أقل بنسبة ${fewer}، وكلّف كل رمز أقل بنسبة ${cheaper}.`,
    mixed: (tokens: string, price: string) => `الرموز المستخدمة: ${tokens}. تكلفة الرمز: ${price}.`,
    carriedOver: (n: string, m: string) => `حمل أول طلب فيها ${n} رمزًا، مقابل ${m} في محاولتك الأولى: يبدو أن الوكيل تابع جلسة سابقة. أغلق Claude Code وشغّله من جديد لمقارنة عادلة.`,
    monthly: (amount: string) => `لو وفّر عملك مع الوكيل النسبة نفسها، لأصبحت فاتورة شهرية قدرها 100 دولار نحو ${amount}.`,
    monthlyNote: 'مثال من هذا المستوى وحده، وليس توقّعًا.',
    varies: 'الوكلاء يتفاوتون: قد يكلّف الوكيل نفسه على المستوى نفسه أكثر أو أقل بوضوح من محاولة إلى أخرى. أعد المحاولة لترى إن كان التوفير ثابتًا.',
    moreNote: 'في مهمة قصيرة يكون النموذج أكبر عامل. تفيد هذه الطرق في العمل الأطول، حيث يتراكم السياق.',
    measuredTitle: 'ما قسناه في هذا المستوى',
    change: { 'smaller-model': 'نموذج أصغر (Haiku)', 'focused-request': 'طلب مركّز' },
    saved: (pct: string) => `أرخص بنسبة ${pct}`,
    measuredNote: en.measuredNote,
    why: {
      'l13-make-the-tests-pass': 'اختبار فاشل يسمّي ملفًا واحدًا عمل قصير وواضح. أصلحه النموذج الأصغر بالجودة نفسها، وكل رمز منه يكلّف نحو الثلث، فالنموذج هنا هو العامل الأكبر.',
      'l14-read-the-stack-trace': 'تتبّع المكدس يشير مباشرة إلى السطر المعطوب، فالعمل قصير وواضح. أصلحه النموذج الأصغر بالجودة نفسها، بنحو ثلث السعر لكل رمز.',
      'l17-who-broke-it': 'التنصيف إجراء من خطوات كثيرة، وقد تاه فيه النموذج الأصغر: احتاج 28 طلبًا حيث احتاج النموذج الكبير 9، فلم تنفعه رموزه الأرخص إلا قليلًا. أما تسمية الطريقة (نصّف بالاختبار ولا تقرأ الفروق) فخفّضت طلبات النموذج الكبير من 23 إلى 13.',
    },
    useWhen: {
      'l13-make-the-tests-pass': 'استخدمه حين يشير الفشل أصلًا إلى موضع الإصلاح.',
      'l14-read-the-stack-trace': 'استخدمه حين يشير الخطأ أصلًا إلى موضع الإصلاح.',
      'l17-who-broke-it': 'استخدمه حين تكون المهمة إجراءً طويلًا: سمِّ الطريقة بدل تصغير النموذج.',
    },
    requestLabel: en.requestLabel,
    stepModel: 'أضف ‎--model haiku في آخر الأمر.',
    stepRequest: en.stepRequest,
    // English only for now (owner, 2026-10-29).
    failedRuns: en.failedRuns,
    dearer: en.dearer,
    inConversation: en.inConversation,
    stepKeep: en.stepKeep,
  },
  de: {
    run1: 'Lauf 1 · dein Agent, wie er ist',
    runAfter: 'Dein Lauf mit einem Tipp',
    cost: 'Was es gekostet hat',
    priced: (book: string) => `Zu API-Listenpreisen (Preisbuch ${book}).`,
    reported: 'Gezählt aus dem, was Claude Code gemeldet hat.',
    tokensIn: 'Gelesene Tokens',
    cached: (n: string) => `davon ${n} aus dem Cache`,
    tokensOut: 'Geschriebene Tokens',
    calls: 'Modellaufrufe',
    byModel: 'Kosten nach Modell',
    analysis: 'Was die Kosten getrieben hat',
    topModel: (model: string, pct: string) => `${pct} der Kosten gingen an ${model}.`,
    cacheShare: (pct: string) => `${pct} dessen, was er gelesen hat, kam aus dem Prompt-Cache, der nur einen Bruchteil kostet.`,
    readRatio: (n: string) => `Er hat ${n} Tokens für jedes geschriebene Token gelesen: Der Großteil der Rechnung ist Kontext, nicht Antworten.`,
    tipsTitle: 'So gibst du beim nächsten Lauf weniger aus',
    pointed: 'Dein Lauf deutet auf diesen Tipp',
    more: 'Weitere Wege',
    stepsTitle: en.stepsTitle,
    step1: 'Wende einen Tipp an. Für den Modell-Tipp hängst du --model haiku an das Ende des Befehls.',
    step2: 'Beende Claude Code, damit sich der nächste Agent an nichts aus diesem Lauf erinnert.',
    step3: en.step3,
    replay: 'Noch einmal',
    savedTitle: 'Du hast gespart',
    lessThan: (pct: string) => `${pct} weniger als dein erster Lauf`,
    moreTitle: 'Das hat mehr gekostet',
    moreThan: (pct: string) => `${pct} mehr als dein erster Lauf. Probier einen anderen Tipp und versuch es noch einmal.`,
    same: 'Genauso teuer wie dein erster Lauf. Probier einen anderen Tipp und versuch es noch einmal.',
    before: 'Erster Lauf',
    after: 'Seitdem',
    honest: 'Jeder Lauf nach dem ersten zählt hier, auch die fehlgeschlagenen.',
    differenceTitle: 'Was den Unterschied gemacht hat',
    firstRun: 'Erster Lauf',
    latestRun: 'Letzter Lauf',
    perMillion: 'Kosten pro Million Tokens',
    modelOnly: (more: string, cheaper: string) => `Dein letzter Lauf hat ${more} mehr Tokens verbraucht, aber jedes Token kostete ${cheaper} weniger. Die Ersparnis kam vom Modell, nicht von weniger Arbeit.`,
    lessWork: (fewer: string) => `Ein Token kostete in beiden Läufen etwa gleich viel; der letzte hat einfach ${fewer} weniger Tokens verbraucht.`,
    both: (fewer: string, cheaper: string) => `Dein letzter Lauf hat ${fewer} weniger Tokens verbraucht, und jedes Token kostete ${cheaper} weniger.`,
    mixed: (tokens: string, price: string) => `Verbrauchte Tokens: ${tokens}. Kosten pro Token: ${price}.`,
    carriedOver: (n: string, m: string) => `Sein erster Aufruf trug schon ${n} Tokens, gegenüber ${m} in deinem ersten Lauf: Der Agent hat offenbar eine frühere Sitzung fortgesetzt. Beende Claude Code und starte es neu, damit der Vergleich fair ist.`,
    monthly: (amount: string) => `Wenn deine Agentenarbeit denselben Anteil spart, käme eine monatliche Rechnung von 100 US$ auf etwa ${amount}.`,
    monthlyNote: 'Ein Beispiel aus diesem einen Level, keine Prognose.',
    varies: 'Agenten schwanken: Derselbe Agent kann im selben Level von Lauf zu Lauf deutlich mehr oder weniger kosten. Versuch es noch einmal, um zu sehen, ob die Ersparnis hält.',
    moreNote: 'Bei einer kurzen Aufgabe ist das Modell der größte Hebel. Diese Wege zahlen sich bei längerer Arbeit aus, wo sich Kontext ansammelt.',
    measuredTitle: 'Was wir auf diesem Level gemessen haben',
    change: { 'smaller-model': 'Ein kleineres Modell (Haiku)', 'focused-request': 'Ein gezielter Auftrag' },
    saved: (pct: string) => `${pct} günstiger`,
    measuredNote: en.measuredNote,
    why: {
      'l13-make-the-tests-pass': 'Ein fehlschlagender Test, der eine Datei nennt, ist kurze, klare Arbeit. Das kleinere Modell hat sie genauso gut erledigt, und jedes seiner Tokens kostet etwa ein Drittel. Hier ist das Modell der Hebel.',
      'l14-read-the-stack-trace': 'Ein Stacktrace zeigt direkt auf die kaputte Zeile, die Arbeit ist also kurz und klar. Das kleinere Modell hat sie genauso gut erledigt, zu etwa einem Drittel des Preises pro Token.',
      'l17-who-broke-it': 'Bisecting ist ein Ablauf aus vielen Schritten, und das kleinere Modell hat sich darin verlaufen: Es brauchte 28 Aufrufe, wo das große Modell 9 brauchte, also halfen die billigeren Tokens kaum. Die Methode zu nennen (mit dem Test bisecten, keine Diffs lesen) senkte das große Modell dagegen von 23 Aufrufen auf 13.',
    },
    useWhen: {
      'l13-make-the-tests-pass': 'Nutze es, wenn der Fehlschlag schon auf die Stelle zeigt, die repariert werden muss.',
      'l14-read-the-stack-trace': 'Nutze es, wenn der Fehler schon auf die Stelle zeigt, die repariert werden muss.',
      'l17-who-broke-it': 'Nutze es, wenn die Aufgabe ein langer Ablauf ist: Nenne die Methode, statt das Modell zu verkleinern.',
    },
    requestLabel: en.requestLabel,
    stepModel: 'Häng --model haiku an das Ende des Befehls.',
    stepRequest: en.stepRequest,
    // English only for now (owner, 2026-10-29).
    failedRuns: en.failedRuns,
    dearer: en.dearer,
    inConversation: en.inConversation,
    stepKeep: en.stepKeep,
  },
  ja: {
    run1: '1回目 · いつものエージェント',
    runAfter: 'ヒントを使った実行',
    cost: 'かかった金額',
    priced: (book: string) => `API の定価で計算(価格表 ${book})。`,
    reported: 'Claude Code が報告した内容から計測しています。',
    tokensIn: '読んだトークン',
    cached: (n: string) => `うち ${n} はキャッシュから`,
    tokensOut: '書いたトークン',
    calls: 'モデル呼び出し',
    byModel: 'モデル別の金額',
    analysis: '金額を押し上げたもの',
    topModel: (model: string, pct: string) => `金額の ${pct} は ${model} によるものです。`,
    cacheShare: (pct: string) => `読んだ量の ${pct} はプロンプトキャッシュからで、料金はごく一部です。`,
    readRatio: (n: string) => `書いた 1 トークンごとに ${n} トークンを読みました。料金の大半は回答ではなくコンテキストです。`,
    tipsTitle: '次の実行で節約する方法',
    pointed: 'この実行に合うヒント',
    more: 'ほかの方法',
    stepsTitle: en.stepsTitle,
    step1: 'ヒントを1つ使います。モデルのヒントなら、コマンドの最後に --model haiku を付けます。',
    step2: 'Claude Code を終了して、次のエージェントがこの実行を何も覚えていないようにします。',
    step3: en.step3,
    replay: 'もう一度',
    savedTitle: '節約できた金額',
    lessThan: (pct: string) => `1回目より ${pct} 少ない`,
    moreTitle: '今回は高くなりました',
    moreThan: (pct: string) => `1回目より ${pct} 多くかかりました。別のヒントでもう一度試してください。`,
    same: '1回目と同じ金額です。別のヒントでもう一度試してください。',
    before: '1回目',
    after: 'それ以降',
    honest: '1回目より後の実行は、失敗したものも含めてすべて数えています。',
    differenceTitle: '違いを生んだもの',
    firstRun: '1回目',
    latestRun: '最新の実行',
    perMillion: '100万トークンあたりの金額',
    modelOnly: (more: string, cheaper: string) => `最新の実行はトークンを ${more} 多く使いましたが、1トークンあたりは ${cheaper} 安くなりました。節約は作業が減ったからではなく、モデルによるものです。`,
    lessWork: (fewer: string) => `1トークンあたりの金額はほぼ同じで、最新の実行は単にトークンを ${fewer} 少なく使いました。`,
    both: (fewer: string, cheaper: string) => `最新の実行はトークンを ${fewer} 少なく使い、1トークンあたりも ${cheaper} 安くなりました。`,
    mixed: (tokens: string, price: string) => `使ったトークン:${tokens}。1トークンあたりの金額:${price}。`,
    carriedOver: (n: string, m: string) => `最初の呼び出しの時点で ${n} トークンあり、1回目は ${m} でした。エージェントが前のセッションを続けたようです。公平に比べるには Claude Code を終了して起動し直してください。`,
    monthly: (amount: string) => `エージェントの作業で同じ割合を節約できれば、月 100 米ドルの請求は約 ${amount} になります。`,
    monthlyNote: 'この1レベルからの例であり、予測ではありません。',
    varies: 'エージェントにはばらつきがあります。同じレベルの同じエージェントでも、実行ごとに金額がかなり上下します。節約が続くか、もう一度試して確かめてください。',
    moreNote: '短い作業では、モデルが最も大きな要因です。これらの方法は、コンテキストがたまる長い作業で効果を発揮します。',
    measuredTitle: 'このレベルで測定した結果',
    change: { 'smaller-model': '小さいモデル（Haiku）', 'focused-request': '絞り込んだ依頼' },
    saved: (pct: string) => `${pct} 安い`,
    measuredNote: en.measuredNote,
    why: {
      'l13-make-the-tests-pass': '1つのファイルを指す失敗テストは、短くはっきりした作業です。小さいモデルも同じように直せて、トークン単価は約3分の1。ここではモデルが効きます。',
      'l14-read-the-stack-trace': 'スタックトレースは壊れた行を直接指すので、作業は短くはっきりしています。小さいモデルも同じように直せて、トークン単価は約3分の1でした。',
      'l17-who-broke-it': 'bisect は多くの手順が続く作業で、小さいモデルは途中で迷いました。大きいモデルの9回に対して28回の呼び出しが必要で、安いトークンはほとんど役に立ちませんでした。代わりに方法を指定する（テストで bisect し、差分を読まない）と、大きいモデルの呼び出しは23回から13回に減りました。',
    },
    useWhen: {
      'l13-make-the-tests-pass': '失敗がすでに直す場所を指しているときに使います。',
      'l14-read-the-stack-trace': 'エラーがすでに直す場所を指しているときに使います。',
      'l17-who-broke-it': '長い手順の作業では、モデルを小さくするより方法を指定します。',
    },
    requestLabel: en.requestLabel,
    stepModel: 'コマンドの最後に --model haiku を付けます。',
    stepRequest: en.stepRequest,
    // English only for now (owner, 2026-10-29).
    failedRuns: en.failedRuns,
    dearer: en.dearer,
    inConversation: en.inConversation,
    stepKeep: en.stepKeep,
  },
};
